import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@mui/material";
import { ChevronDown } from "lucide-react";
import useIsDarkTheme from "../../lib/hooks/useIsDarkTheme";

export default function FeatureFilterSelect({ options, value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const isDark = useIsDarkTheme();
  const open = Boolean(anchorEl);
  const selectedValues = Array.isArray(value) ? value : [];
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const orderedOptions = useMemo(() => {
    const selected = [];
    const unselected = [];

    options.forEach(option => {
      if (selectedSet.has(option)) {
        selected.push(option);
      } else {
        unselected.push(option);
      }
    });

    return [...selected, ...unselected];
  }, [options, selectedSet]);

  const handleToggle = option => {
    if (selectedSet.has(option)) {
      onChange(selectedValues.filter(item => item !== option));
      return;
    }

    onChange([...selectedValues, option]);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        fullWidth
        onClick={event => setAnchorEl(event.currentTarget)}
        endIcon={
          <ChevronDown
            size={16}
            style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
          />
        }
        sx={{
          justifyContent: "space-between",
          textTransform: "none",
          borderRadius: "8px",
          backgroundColor: isDark ? "#111827" : "#fff",
          color: selectedValues.length > 0 ? "var(--color-accent-active)" : isDark ? "#e5e7eb" : "#374151",
          borderColor:
            open || selectedValues.length > 0 ? "var(--color-accent-active)" : isDark ? "#374151" : "#e5e7eb",
          fontSize: "0.8rem",
          fontWeight: 500,
          minHeight: 36,
          px: 1,
          py: 0.55,
          "&:hover": {
            borderColor: "var(--color-accent-active)",
            backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
          },
          "& .MuiButton-endIcon": {
            marginLeft: 1,
            color: "inherit",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            fontSize: "0.8rem",
          }}
        >
          {selectedValues.length === 0
            ? "Feature"
            : `${selectedValues.length} feature${selectedValues.length > 1 ? "s" : ""} selected`}
        </Box>
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 280,
            maxWidth: 320,
            maxHeight: 360,
            borderRadius: "10px",
            border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
            background: isDark ? "#1f2937" : "#fff",
            boxShadow: isDark ? "0 12px 36px rgba(0, 0, 0, 0.35)" : "0 12px 36px rgba(15, 23, 42, 0.12)",
          },
        }}
      >
        <List
          dense
          sx={{
            py: 0.5,
            maxHeight: 360,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: isDark ? "#4b5563" : "#d1d5db",
              borderRadius: 4,
            },
          }}
        >
          {orderedOptions.map(option => {
            const isSelected = selectedSet.has(option);

            return (
              <ListItem key={option} disablePadding>
                <ListItemButton
                  onClick={() => handleToggle(option)}
                  sx={{
                    alignItems: "center",
                    px: 0.75,
                    py: 0.45,
                    gap: 0.25,
                    backgroundColor: isSelected
                      ? isDark
                        ? "var(--color-accent-alpha-08)"
                        : "var(--color-accent-lightest)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isDark ? "var(--color-accent-alpha-08)" : "var(--color-accent-lightest)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 26, m: 0 }}>
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{
                        p: 0.35,
                        color: isDark ? "#6b7280" : "#d1d5db",
                        "&.Mui-checked": { color: "var(--color-accent-active)" },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ my: 0, minWidth: 0 }}
                    primary={option}
                    primaryTypographyProps={{
                      fontSize: "0.8rem",
                      lineHeight: 1.2,
                      color: isSelected ? (isDark ? "#f9fafb" : "#1f2937") : isDark ? "#d1d5db" : "#374151",
                      fontWeight: isSelected ? 600 : 400,
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>
    </>
  );
}

FeatureFilterSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
};
